import { ChangeEvent, useEffect, useState } from "react";
import { formatBytes, getFileIcon } from "../../common";
import { MAX_FILE_SIZE } from "../../constant";
import { useUploadPrivateDocument } from "../../hooks/query/useUpdatePrivateDocument";
import ListUserPopup from "../ListUserPopup";
import { useGetAllSubject } from "../../hooks/query/useGetAllSubject";

interface UploadPopupProps {
    onClose: () => void;
    onSuccess: () => void;
    isPrivate?: boolean;
}
interface UploadDocumentParams {
    subjectId: number;
    gradeLevel: number;
    name: string;
    file: File;
    isPrivate?: boolean;
    emails?: string[];
}

function UploadPopup(props: UploadPopupProps) {
    const { onClose, onSuccess, isPrivate } = props;
    // const { user } = useSelector((state: RootState) => state.auth);
    const [show, setShow] = useState(false);
    const [subject, setSubject] = useState(0);
    const [gradeLevel, setGradeLevel] = useState(0);
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState("");
    const [openUserPopup, setOpenUserPopup] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const { data: subjectsRaw } = useGetAllSubject(); // 👈
    const subjects = Array.isArray(subjectsRaw) ? subjectsRaw : []; // 👈

    useEffect(() => {
        setTimeout(() => setShow(true), 10);
    }, []);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            setShow(false);
            setTimeout(onClose, 300);
        }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        if (selectedFile.size > MAX_FILE_SIZE) {
            setError("❌ File vượt quá 30MB. Vui lòng chọn file nhỏ hơn.");
            setFile(null);
            return;
        }

        if (error) setError("");
        setFile(selectedFile);
    };

    const uploadPrivateDocument = useUploadPrivateDocument();

    const handleSubmit = async () => {
        if (subject === 0) {
            setError("❌ Vui lòng chọn môn học.");
            return;
        }

        if (gradeLevel === 0) {
            setError("❌ Vui lòng chọn lớp.");
            return;
        }

        if (!file) {
            setError("❌ Vui lòng chọn file.");
            return;
        }

        setError("");
        const nameWithoutExt = file.name.split(".").slice(0, -1).join(".");
        let params: UploadDocumentParams = {
            subjectId: subject,
            gradeLevel,
            name: nameWithoutExt,
            file,
        }
        if (isPrivate) {
            params.isPrivate = true;
            params.emails = selectedUsers
                .map(user => user.email)
                .filter(Boolean);
        }
        console.log("params", params);
        uploadPrivateDocument.mutate(params, {
            onSuccess: () => {
                onSuccess();
                setShow(false);
                setTimeout(onClose, 300);
            },
        });
    };

    // const { data: listUser } = useGetAllUser();

    const handleUserData = (data: any[]) => {
        const combined = [...selectedUsers, ...data];
        const uniqueData = Array.from(
            new Map(combined.map((item) => [item.id, item])).values()
        );
        setSelectedUsers(uniqueData);
    };

    const handleRemoveUser = (userId: number) => {
        setSelectedUsers(selectedUsers.filter((u) => u.id !== userId));
    };

    return (
        <>
            {openUserPopup && (
                <ListUserPopup
                    onData={handleUserData}
                    openPopup={openUserPopup}
                    onClose={() => setOpenUserPopup(false)}
                    onCancel={() => setOpenUserPopup(false)}
                />
            )}

            {/* Overlay */}
            <div
                className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-50 flex justify-center items-center transition-opacity duration-300"
                onClick={handleOverlayClick}
                style={{ opacity: show ? 1 : 0 }}
            >
                {/* Popup */}
                <div
                    className={`relative w-[90%] lg:w-[500px] max-h-[80vh] bg-white rounded-box flex flex-col
          transition-all duration-300
          ${show ? "translate-y-0 opacity-100" : "-translate-y-10 opacity-0"}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <h2 className="text-secondary font-bold text-[20px] py-[20px] text-center border-b">
                        Upload tài liệu
                    </h2>

                    {/* Body - SCROLL */}
                    <div className="flex-1 overflow-y-auto px-[24px] py-[16px] custom-scroll">
                        {/* Môn học */}
                       <div className="mb-[20px]">
                            <label className="font-[600] inline-block mb-[6px]">
                                Môn học
                            </label>
                            <select
                                className="outline-none border border-[#e0e0e0] rounded-[4px] px-[8px] w-full h-[46px]"
                                value={subject}
                                onChange={(e) => {
                                    setSubject(Number(e.target.value));
                                    if (error) setError("");
                                }}
                            >
                                <option value={0}>Chọn môn học</option>
                                {subjects.map((s: any) => ( // 👈
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Lớp */}
                        <div className="mb-[20px]">
                            <label className="font-[600] inline-block mb-[6px]">Lớp</label>
                            <select
                                className="outline-none border border-[#e0e0e0] rounded-[4px] px-[8px] w-full h-[46px]"
                                value={gradeLevel}
                                onChange={(e) => {
                                    setGradeLevel(Number(e.target.value));
                                    if (error) setError("");
                                }}
                            >
                                <option value={0}>Chọn lớp</option>
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Chọn file */}
                        <div className="mb-[20px]">
                            <label className="font-[600] inline-block mb-[6px]">
                                Chọn file
                            </label>

                            <label className="flex justify-center items-center w-full h-[150px] border-2 border-dashed border-[#e0e0e0] rounded-[4px] cursor-pointer hover:border-blue-500">
                                <input type="file" className="hidden" onChange={handleFileChange} />

                                {!file ? (
                                    <div className="text-center">
                                        <i className="fa-solid fa-upload text-[30px] text-blue-500 mb-[10px]" />
                                        <p>Kích thước tối đa 30MB</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-[6px]">
                                        <div className="flex items-center gap-[10px]">
                                            {getFileIcon(file.name)}
                                            <p className="text-gray-700 text-[14px]">
                                                {file.name}
                                            </p>
                                            <i
                                                className="fa-solid fa-xmark text-red-500 cursor-pointer"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setFile(null);
                                                }}
                                            />
                                        </div>
                                        <p className="text-gray-500 text-[13px]">
                                            {formatBytes(file.size)}
                                        </p>
                                    </div>
                                )}
                            </label>
                        </div>

                        {/* Private user */}
                        {isPrivate && (
                            <div className="mb-[20px]">
                                <label className="font-[600] inline-block mb-[6px]">
                                    Danh sách người dùng được xem tài liệu *
                                </label>

                                <div className="border rounded-[4px]">
                                    <button
                                        onClick={() => setOpenUserPopup(true)}
                                        className="w-full h-[40px] bg-secondary text-white hover:opacity-90"
                                    >
                                        Thêm
                                    </button>

                                    <div className="max-h-[120px] overflow-y-auto custom-scroll">
                                        {selectedUsers.length ? (
                                            selectedUsers.map((user) => (
                                                <p
                                                    key={user.id}
                                                    className="px-[8px] py-[4px] flex justify-between items-center hover:bg-gray-100"
                                                >
                                                    <span>{user.fullName || user.email}</span>
                                                    <i
                                                        onClick={() => handleRemoveUser(user.id)}
                                                        className="fa fa-times cursor-pointer"
                                                    />
                                                </p>
                                            ))
                                        ) : (
                                            <p className="text-center text-gray-400 py-[12px]">
                                                Chưa có user nào
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        <p className="text-red-500 text-center">{error}</p>
                    </div>

                    {/* Footer */}
                    <div className="border-t p-[16px] flex justify-center">
                        <button
                            onClick={handleSubmit}
                            className="bg-blue-500 text-white py-[10px] px-[30px] rounded-[6px] hover:bg-blue-600 transition"
                        >
                            Upload
                        </button>
                    </div>

                    {/* Close */}
                    <button
                        className="absolute top-[12px] right-[12px]"
                        onClick={() => setShow(false)}
                    >
                        <i className="fa-solid fa-xmark text-[22px] text-blue-500" />
                    </button>
                </div>
            </div>
        </>
    );
}

export default UploadPopup;
